import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import Question from '../../../views/ProductDetails/Series3CollarDetails/components/FAQ/Question';
import styles from './MiniFAQ.module.scss';
import { ReactComponent as IconHelp } from '../../../assets/images/icons/icon_help.svg';
import { useFeatureValue } from '@growthbook/growthbook-react';
import { showNanoVariant } from '../../../components/NanoBanner/NanoBanner';
import { closeDecagonChat, openDecagonChat } from '../../../components/AppBar/NavBar';
import { series3CollarFAQChatButtonClick } from '../../../lib/analytics/events';
import { Species } from '../../../types/gql-op-types';
import { showRescueGwaVariant } from '../../../views/RescueLanding/RescueBanner';

export default function FAQ({
  species,
  containerRef,
}: {
  species: Species;
  containerRef?: React.RefObject<HTMLDivElement>;
}) {
  const { t } = useTranslation();
  const decagonChatEnabled = useFeatureValue('decagon-chat-widget', false);
  const showDecagonChat = showNanoVariant() && decagonChatEnabled;
  const rescueVariant = showRescueGwaVariant();

  useEffect(() => {
    const handleMessage = (event: any) => {
      if (event.origin !== 'https://decagon.ai') return;

      const { data } = event;
      if (data.type === 'DECAGON_INACTIVE') {
        closeDecagonChat();
      }
    };

    window.addEventListener('message', handleMessage);
    return () => {
      window.removeEventListener('message', handleMessage);
      // Close the chat when navigating away
      closeDecagonChat();
    };
  }, []);

  // an adopter has already been given the device and the membership, so the three questions about
  // choosing between devices, upgrading an existing membership and reusing an existing pet profile
  // do not describe anything they can act on. they are dropped so the offer questions stay reachable.
  const questions: { title: string; body: React.ReactNode }[] = [
    ...(rescueVariant
      ? [
          { title: t('rescue_faq_when_six_months_end'), body: t('rescue_faq_when_six_months_end_answer') },
          { title: t('rescue_faq_unknown_age_or_breed'), body: t('rescue_faq_unknown_age_or_breed_answer') },
        ]
      : []),
    {
      title: t('whats_the_weight_and_dimensions_of_the_fi_mini'),
      body: t('fi_mini_weight_and_dimensions_answer'),
    },
    {
      title: t('what_types_of_collars_or_harnesses_does_the_fi_mini_fit_on'),
      body: (
        <>
          {t('fi_mini_collar_harness_fit_answer')}
          <ul>
            <li>{t('strap_minimum_none_maximum_1_25')}</li>
            <li>{t('soft_case_minimum_0_375_maximum_1_25')}</li>
            <li>{t('3_8_soft_case_minimum_0_375_maximum_0_375')}</li>
          </ul>
        </>
      ),
    },
    {
      title: t('do_i_need_to_have_a_verizon_account_to_use_a_fi_mini'),
      body: t('verizon_account_not_required_answer'),
    },
    ...(rescueVariant
      ? []
      : [
          {
            title: t('can_i_connect_my_pets_existing_fi_profile_to_a_new_fi_mini'),
            body: t('connect_existing_fi_profile_answer'),
          },
        ]),
    {
      title: t('why_is_fi_a_membership'),
      body: (
        <>
          {t('why_is_fi_a_membership_answer_part1')}
          <br />
          <br />
          {t('why_is_fi_a_membership_answer_part2')}
        </>
      ),
    },
    ...(rescueVariant
      ? []
      : [
          {
            title: t('can_i_get_a_new_device_if_im_already_a_fi_member'),
            body: t('can_i_get_a_new_device_if_im_already_a_fi_member_answer'),
          },
          {
            title: t('how_do_i_know_if_the_fi_mini_or_series_3_plus_is_right_for_me'),
            body: t('fi_mini_or_series_3_plus_right_for_me_answer'),
          },
        ]),
  ];

  return (
    <div ref={containerRef} className={styles.container}>
      <h2 className={styles.heading}>{t('faq')}</h2>
      <div>
        {questions.map((question, index) => (
          // index is the array position rather than a per-question constant: FAQExpand reports it
          // as the analytics position, so it has to stay contiguous when the rescue set swaps in.
          <Question key={question.title} title={question.title} page="Mini" index={index}>
            {question.body}
          </Question>
        ))}
      </div>
      {showDecagonChat && (
        <div className={styles.decagonChatContainer}>
          <IconHelp />
          <div className={styles.decagonChatTitle}>{t('something_else_on_your_mind')}</div>
          <button
            className={styles.decagonChatButton}
            onClick={() => {
              series3CollarFAQChatButtonClick();
              openDecagonChat();
            }}
          >
            {t('chat_with_fi')}
          </button>
        </div>
      )}
    </div>
  );
}
